import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type OrganizationSwitchStatus = 'busy' | 'unsaved-input' | 'switched';

export type OrganizationSwitchOptions = {
  discardUnsavedInput?: boolean;
  /** 切り替え後の遷移先（通知のディープリンク用）。対象組織のスコープ内パスのときだけ有効。 */
  path?: string;
};

export type OrganizationScopeContextValue = {
  organizationId: number;
  isBusy: boolean;
  beginBusy: () => void;
  endBusy: () => void;
  registerUnsavedInputCheck: (
    check: () => boolean,
    discard?: () => void,
  ) => () => void;
  switchOrganization: (
    targetOrganizationId: number,
    options?: OrganizationSwitchOptions,
  ) => Promise<OrganizationSwitchStatus>;
};

export const OrganizationScopeContext =
  createContext<OrganizationScopeContextValue | null>(null);

export function useOrganizationScope(): OrganizationScopeContextValue {
  const value = useContext(OrganizationScopeContext);
  if (!value) {
    throw new Error(
      'useOrganizationScope は <OrganizationScope> 内で使用する必要があります',
    );
  }
  return value;
}

export function useOrganizationScopeOrNull(): OrganizationScopeContextValue | null {
  return useContext(OrganizationScopeContext);
}

export function useOrganizationId(): number {
  return useOrganizationScope().organizationId;
}

const noop = (): void => {
  // OrganizationScope 外では何もしない
};

/** 進行中操作のカウンター。組織切り替えの安全判定に使う。スコープ外では no-op。 */
export function useOrganizationBusy(): {
  isBusy: boolean;
  beginBusy: () => void;
  endBusy: () => void;
} {
  const value = useContext(OrganizationScopeContext);
  return {
    isBusy: value?.isBusy ?? false,
    beginBusy: value?.beginBusy ?? noop,
    endBusy: value?.endBusy ?? noop,
  };
}

/**
 * 未保存の入力を組織切り替え前に確認させる。スコープ外では no-op。
 * 入力ごとに登録し直さないよう、最新の判定は ref 経由で読む。
 */
export function useUnsavedInputGuard(
  hasUnsavedInput: () => boolean,
  onDiscard?: () => void,
): void {
  const registerUnsavedInputCheck =
    useContext(OrganizationScopeContext)?.registerUnsavedInputCheck;
  const latestCheckRef = useRef(hasUnsavedInput);
  const latestDiscardRef = useRef(onDiscard);

  useEffect(() => {
    latestCheckRef.current = hasUnsavedInput;
    latestDiscardRef.current = onDiscard;
  });

  useEffect(() => {
    if (!registerUnsavedInputCheck) return;
    return registerUnsavedInputCheck(
      () => latestCheckRef.current(),
      () => latestDiscardRef.current?.(),
    );
  }, [registerUnsavedInputCheck]);
}

/** 読み込み・保存直後の snapshot から変わったあいだ破棄確認を出す。markSaved は内容を差し替える更新と同じ batch で呼ぶ。 */
export function useUnsavedChangesGuard(
  snapshot: string,
  pending = false,
): () => void {
  const [savedSnapshot, setSavedSnapshot] = useState(snapshot);
  const [snapshotOutdated, setSnapshotOutdated] = useState(false);
  if (snapshotOutdated) {
    setSnapshotOutdated(false);
    setSavedSnapshot(snapshot);
  }
  useUnsavedInputGuard(() => pending || snapshot !== savedSnapshot);
  return useCallback(() => setSnapshotOutdated(true), []);
}
